import React, { useState, useEffect, useCallback } from 'react';
import { Music, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const SpotifyAuth = ({ onAuthenticated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);

  // Check if already connected on mount
  const checkConnection = useCallback(async () => {
    try {
      setIsCheckingStatus(true);
      const response = await api.get('/api/auth/spotify/status');
      if (response.data.connected && !response.data.isExpired) {
        setIsConnected(true);
        onAuthenticated();
      } else if (response.data.connected && response.data.isExpired) {
        // Token expired — try to refresh server-side (server handles it on next search)
        setIsConnected(true);
        onAuthenticated();
      }
    } catch (err) {
      console.log('[SpotifyAuth] Status check failed:', err.message);
    } finally {
      setIsCheckingStatus(false);
    }
  }, [onAuthenticated]);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  // Listen for OAuth callback from popup
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'spotify-auth-success') {
        setIsConnected(true);
        setError(null);
        onAuthenticated();
        toast.success('Connected to Spotify!');
      } else if (event.data?.type === 'spotify-auth-error') {
        setError({ title: 'Auth Failed', message: 'Spotify authorization failed. Please try again.' });
        toast.error('Spotify authorization failed');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onAuthenticated]);

  const handleConnect = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get the auth URL from our server
      const response = await api.get('/api/auth/spotify/login');
      const { authUrl } = response.data;

      if (!authUrl) {
        throw new Error('No auth URL received from server');
      }

      // Open popup for OAuth
      const width = 500;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        'spotify-auth',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
      );

      // Poll for popup close
      const pollTimer = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(pollTimer);
          setIsLoading(false);
          // Re-check connection status after popup closes
          checkConnection();
        }
      }, 500);
    } catch (err) {
      console.error('[SpotifyAuth] Connect error:', err);
      setError({
        title: 'Connection Error',
        message: err.response?.data?.error || err.message || 'Failed to start Spotify authorization'
      });
      setIsLoading(false);
    }
  };

  if (isCheckingStatus) {
    return (
      <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
        <div className="flex items-center justify-center py-4 text-gray-400">
          <Loader className="w-5 h-5 animate-spin mr-2" />
          <span>Checking Spotify connection...</span>
        </div>
      </div>
    );
  }

  if (isConnected) {
    return (
      <div className="bg-green-600/20 border border-green-500 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-400 font-medium">Connected to Spotify</span>
        </div>
        <p className="text-xs text-green-300 mt-2">
          Your Spotify account is linked. Search and playback are ready.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
      <div className="flex items-center space-x-3 mb-4">
        <Music className="w-6 h-6 text-green-400" />
        <h3 className="text-lg font-semibold text-white">Connect Spotify</h3>
      </div>

      {error ? (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-300 font-medium">{error.title}</p>
              <p className="text-red-200 text-sm mt-1">{error.message}</p>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-gray-300 mb-4">
          Connect your Spotify Premium account to search songs and control playback.
        </p>
      )}

      <button
        onClick={handleConnect}
        disabled={isLoading}
        className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>Connecting...</span>
          </>
        ) : (
          <>
            <Music className="w-4 h-4" />
            <span>{error ? 'Try Again' : 'Connect Spotify'}</span>
          </>
        )}
      </button>

      <p className="text-gray-400 text-xs mt-3">
        Spotify Premium is required for in-browser playback.
      </p>
    </div>
  );
};

export default SpotifyAuth;
